import { PayloadAction, createSlice } from '@reduxjs/toolkit'
import { Show, MuxStreamActiveType, VideoOnDemandStatus, VideoOnDemand } from 'data/Show'
import { showIsSoldOut } from 'utils/show'
import { RootState } from 'redux/store'
import { createSelector } from 'reselect'
import { Video, VideoStatus } from 'data/Video'
import moment from 'moment'
import { isPopulated, isPopulatedArray } from 'utils/populated'
import { WidgetType } from 'data/Show'
import { PassTierRemainingPasses, PassTier } from 'data/PassTier'
import { PlayerStates } from 'data/VideoPlayer'

export const PREVIEW_TIMEOUT_MS = 120000

export enum PreviewState {
    UNSTARTED,
    STARTED,
    ENDED,
}

type RemainingPasses = number | null

interface LiveShowState {
    show: Show | null
    passTiers: PassTier[]
    willForceLive: boolean
    forceLive: boolean
    showHasStarted: boolean
    shouldPlayPreRoll: boolean
    preview: {
        state: PreviewState
        endTime: string | null
    }
    videoOnDemand: VideoOnDemand | null
    video: Video
    tribute: {
        sending: boolean
        result: 'success' | 'error' | null
        message: string | null
    }
    pageIsFullyLoaded: boolean
    remainingPasses: RemainingPasses
    initialPlayerState: PlayerStates
}

export const initialState: LiveShowState = {
    show: null,
    passTiers: [],
    willForceLive: false,
    forceLive: false,
    showHasStarted: false,
    shouldPlayPreRoll: false,
    preview: {
        state: PreviewState.UNSTARTED,
        endTime: null,
    },
    videoOnDemand: null,
    video: {
        status: VideoStatus.UNKNOWN,
        updatedAt: moment().format(),
    },
    tribute: {
        sending: false,
        result: null,
        message: null,
    },
    pageIsFullyLoaded: false,
    remainingPasses: null,
    initialPlayerState: PlayerStates.LOADING,
}

const liveShowSlice = createSlice({
    name: 'liveShow',
    initialState,
    reducers: {
        resetState: _ => initialState,
        showReceived: (state, { payload: show }: PayloadAction<Show>) => {
            state.show = show
            state.shouldPlayPreRoll =
                !!show.hypeVideoVOD &&
                isPopulated(show.hypeVideoVOD) &&
                show.hypeVideoVOD.status === VideoOnDemandStatus.READY
        },
        showStarted: state => {
            state.showHasStarted = true
            state.shouldPlayPreRoll = false
        },
        preShowPreRollEnded: state => {
            state.shouldPlayPreRoll = false
        },
        videoReceived: (state, { payload: video }: PayloadAction<Video>) => {
            state.video = video
        },
        videoPlayerError: state => {
            state.video = { status: VideoStatus.ERROR, updatedAt: moment().format() }
        },
        pageIsLoaded: state => {
            state.pageIsFullyLoaded = true
        },
        updateRemainingPasses: (state, { payload: remainingPasses }: PayloadAction<RemainingPasses>) => {
            state.remainingPasses = remainingPasses
        },
        updateActiveMuxStream: (state, { payload: active }: PayloadAction<MuxStreamActiveType>) => {
            state.show!.muxStream.active = active
        },
        sendingTribute: state => {
            state.tribute = {
                sending: true,
                result: null,
                message: null,
            }
        },
        sendTributeResponse: (
            state,
            {
                payload: { result, message },
            }: PayloadAction<{
                result: 'success' | 'error'
                message: string
            }>,
        ) => {
            state.tribute = {
                sending: false,
                result,
                message,
            }
        },
        setInitialVideoOnDemand: (state, { payload: vod }: PayloadAction<VideoOnDemand>) => {
            state.videoOnDemand = vod
        },
        setVideoOnDemand: (state, { payload: playbackId }: PayloadAction<string>) => {
            const vod =
                isPopulatedArray(state.show?.videosOnDemand) &&
                state.show?.videosOnDemand.find(vod => vod.playbackId === playbackId)

            if (vod) {
                state.videoOnDemand = vod
            }
        },
        previewStarted: (state, { payload: endTime }: PayloadAction<string>) => {
            state.preview = {
                state: PreviewState.STARTED,
                endTime,
            }
        },
        previewEnded: state => {
            state.preview = {
                state: PreviewState.ENDED,
                endTime: null,
            }
        },
        willForceVideoToLive: state => {
            state.willForceLive = true
        },
        forceVideoToLive: state => {
            state.forceLive = true
        },
        passTiersReceived: (state, { payload: passTiers }: PayloadAction<PassTier[]>) => {
            state.passTiers = passTiers
        },
        updatePassTiersStatus: (state, { payload: passTiersLimit }: PayloadAction<PassTierRemainingPasses[]>) => {
            if (isPopulatedArray(state.passTiers)) {
                state.passTiers = state.passTiers.map(passTier => {
                    const limit = passTiersLimit.find(limit => limit.passTier === passTier._id)
                    return {
                        ...passTier,
                        isSoldOut:
                            !!limit &&
                            limit.remaining !== null &&
                            limit.remaining !== undefined &&
                            limit.remaining <= 0,
                    }
                })
            }
        },
        passTierHasSoldOut: (state, { payload: passTierId }: PayloadAction<string>) => {
            const passTierIndex = state.passTiers.findIndex(tier => tier._id === passTierId)
            if (passTierIndex !== -1) {
                state.passTiers[passTierIndex].isSoldOut = true
            }
        },
        showHasSoldOut: state => {
            state.remainingPasses = 0
            state.passTiers = state.passTiers.map(passTier => ({
                ...passTier,
                isSoldOut: true,
            }))
        },
    },
    extraReducers: {
        'liveShow/retrieveLiveData/fulfilled': state => {
            state.pageIsFullyLoaded = true
        },
    },
})

const { actions, reducer } = liveShowSlice // Extract the action creators object and the reducer

export const {
    resetState,
    showReceived,
    videoReceived,
    updateRemainingPasses,
    updateActiveMuxStream,
    sendingTribute,
    sendTributeResponse,
    videoPlayerError,
    setVideoOnDemand,
    setInitialVideoOnDemand,
    showStarted,
    preShowPreRollEnded,
    previewStarted,
    previewEnded,
    willForceVideoToLive,
    forceVideoToLive,
    passTiersReceived,
    updatePassTiersStatus,
    passTierHasSoldOut,
    showHasSoldOut,
    pageIsLoaded,
} = actions

export default reducer

export const getLiveShow = (store: RootState) => store.liveShow

export const getShow = createSelector(getLiveShow, live => live.show)

export const getShowArtist = createSelector(getShow, show => show?.artist)

export const getShowWidgets = createSelector(getShow, show => show?.widgets)

export const getShowStartTime = createSelector(getShow, show => (show ? moment(show.startTime).format() : undefined))

export const getShowEndTime = createSelector(getShow, show => (show ? moment(show.endTime).format() : undefined))

export const getShowHasStarted = createSelector(getLiveShow, live => live.showHasStarted)

export const getShouldPlayPreRoll = createSelector(getLiveShow, live => live.shouldPlayPreRoll)

export const getVideo = createSelector(getLiveShow, live => live.video)

export const getPageIsFullyLoaded = createSelector(getLiveShow, live => live.pageIsFullyLoaded)

export const getRemainingPasses = createSelector(getLiveShow, live => {
    const remainingPasses = live.remainingPasses
    // if we set up viewerCap after we created the show and before that some users purchased the ticket,
    // it is possible that remainingPasses will be a negative value.. So in that case, we just return 0
    if (remainingPasses !== null && remainingPasses < 0) {
        return 0
    }
    return remainingPasses
})

export const getShowIsSoldOut = createSelector(getRemainingPasses, remainingPasses => showIsSoldOut(remainingPasses))

export const getVideoOnDemand = createSelector(getLiveShow, live => live.videoOnDemand)

export const getArtistBuyTypeWidgets = createSelector(getShow, show => {
    if (isPopulatedArray(show?.widgets)) {
        return show?.widgets.filter(w => w.type === WidgetType.BUY)
    }
    return []
})

export const getExplicitContentFlag = createSelector(getShow, show => show?.hasExplicitContent)

export const getShowPassTiers = createSelector(getLiveShow, live => live.passTiers)

export const getShowPreview = createSelector(getLiveShow, live => live.preview)

export const getWillForceVideoToLive = createSelector(getLiveShow, live => live.willForceLive)

export const getForceVideoToLive = createSelector(getLiveShow, live => live.forceLive)

export const getTributeValues = createSelector(getLiveShow, live => live.tribute)

export const getActiveMuxStream = createSelector(getLiveShow, live => {
    return live.show!.muxStream[live.show!.muxStream.active] || live.show!.muxStream[MuxStreamActiveType.PRIMARY]
})

export const isPaywallDisabled = createSelector(getShow, show => (show ? show.disablePaywall : false))

export const getIsEnabledAta = createSelector(getShow, show => show?.enabledAta)

export const getInitialPlayerState = createSelector(getLiveShow, live => live.initialPlayerState)
